import { useHead, useStripTags } from '#imports'
import { unref, type Ref, type ComputedRef } from 'vue'
import { useI18n } from '#i18n'
import { getSiteNames } from '~/lib/constants'

/* eslint-disable @typescript-eslint/no-explicit-any */
export function useSeoFromEntity(
  entityRef: Ref<any | null> | ComputedRef<any | null>,
  baseUrl: string,
  locale: string = 'ru'
) {
  const { t } = useI18n()
  const siteNames = getSiteNames()

  function interpolate(template: string, entity: Record<string, any>): string {
    return template.replace(/:(\w+)\.([\w.]+):/g, (_, prefix, key) => {
      const parts = key.split('.')
      let value = entity
      for (const part of parts) {
        value = value?.[part]
        if (value === undefined || value === null) return 'NOT_FOUND_ENTITY_PROPERTY'
      }
      return String(value)
    })
  }

  // Use global SEO data from app.vue to avoid duplicate requests
  const nuxtApp = useNuxtApp()

  // Fallback fetch for when the global state isn't available — must be called
  // in setup scope (Nuxt composable rule), not inside the computed() below.
  // The handler resolves to null without a request once global SEO data is present.
  // useFetch cannot be used here: it sends a null URL as the relative path "null".
  const { data: fallbackSeoData } = useLazyAsyncData(
    () => `seo-entity-${unref(entityRef)?.url}-${locale}`,
    () => {
      if (nuxtApp._seoData?.value) return Promise.resolve(null)

      const entity = unref(entityRef)
      return entity?.url
        ? $fetch(`/api/seo?path=${entity.url}&lang=${locale}`)
        : Promise.resolve(null)
    },
    {
      default: () => null
    }
  )

  const seoApiData = computed(() => {
    // Try to get SEO data from app's global state first
    if (nuxtApp._seoData?.value) {
      return nuxtApp._seoData.value
    }

    return fallbackSeoData.value
  })

  useHead(() => {
    const entity = unref(entityRef)

    if (!entity) {
      return {
        title: t('seo_from_entity.default.title'),
        meta: [
          {
            name: 'description',
            content: t('seo_from_entity.default.description')
          }
        ]
      }
    }

    const matchedSeo = seoApiData.value

    const titleOfficialPart = entity.official_title
      ? `, ${entity.official_title}`
      : entity.organization?.title
        ? `, ${entity.organization.title}`
        : ''

    const fullTitle = matchedSeo?.meta_title
      ? interpolate(matchedSeo.meta_title, entity)
      : `${entity.title}${titleOfficialPart}`

    const ogTitleContent = matchedSeo?.meta_title
      ? interpolate(matchedSeo.meta_title, entity)
      : `${entity.title}${titleOfficialPart}`

    const metaDescription = entity.meta_description
      ? entity.meta_description
      : matchedSeo?.meta_description
        ? interpolate(matchedSeo.meta_description, entity)
        : useStripTags(entity.description) || ''

    const metaKeywords = entity.meta_keywords
      ? entity.meta_keywords
      : matchedSeo?.meta_keywords
        ? interpolate(matchedSeo.meta_keywords, entity)
        : ''

    const ogDescription = matchedSeo?.og_description
      ? interpolate(matchedSeo.og_description, entity)
      : metaDescription

    const image = entity.logo ?? entity.largeImage

    return {
      htmlAttrs: {
        lang: locale
      },
      title: `${fullTitle} ${t('seo.suffix.banks_kyrgyzstan')}`,
      meta: [
        {
          name: 'description',
          content: metaDescription
        },
        {
          name: 'keywords',
          content: metaKeywords
        },
        {
          property: 'og:title',
          content: ogTitleContent
        },
        {
          property: 'og:description',
          content: ogDescription
        },
        { property: 'og:type', content: 'article' },
        { property: 'og:locale', content: locale === 'ky' ? 'ky_KG' : 'ru_RU' },
        { property: 'og:site_name', content: siteNames[locale] },
        { property: 'og:logo', content: `${baseUrl}/images/logos/main_logo_banks.png` },
        entity.url && { property: 'og:url', content: `${baseUrl}${entity.url}` },

        image && { property: 'og:image', content: image },
        image && { property: 'og:image:url', content: image },
        image && { property: 'og:image:width', content: '1200' },
        image && { property: 'og:image:height', content: '630' },
        image && { property: 'og:image:type', content: 'image/jpeg' },

        { name: 'twitter:card', content: 'summary_large_image' },
        {
          property: 'twitter:title',
          content: ogTitleContent
        },
        {
          name: 'twitter:description',
          content: metaDescription
        },
        { name: 'twitter:site', content: '@banks_kg' },
        image && { name: 'twitter:image', content: image }
      ].filter(Boolean)
    }
  })
}
